import { Component, Inject, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  Validators
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ShopSettingsRepository } from '@infrab4a/connect';
import { MessageService, PrimeNGConfig } from 'primeng/api';
import { FileUpload } from 'primeng/fileupload';
import { FileControllerService } from 'src/app/admin-api';
import { CustomizationFormComponent } from 'src/app/components/customization/customization-form.component';
import { ImageService } from 'src/app/services/image.service';
import {
  Campaign,
  CampaignsDocument,
  CampaignsUpdatePayload
} from '../glam-campaigns.models';

@Component({
  selector: 'app-glam-campaigns-form',
  templateUrl: './glam-campaigns-form.component.html',
  styleUrls: ['./glam-campaigns-form.component.scss']
})
export class GlamCampaignsFormComponent
  extends CustomizationFormComponent
  implements OnInit
{
  private static readonly TITLE_MAX_LENGTH = 80;
  private static readonly DESCRIPTION_MAX_LENGTH = 210;

  pageDocument: CampaignsDocument;
  campaigns: Campaign[] = [];
  activeCampaign: Campaign | null = null;

  constructor(
    @Inject('ShopSettingsRepository')
    private shopSettingsRepository: ShopSettingsRepository,
    private formBuilder: FormBuilder,
    private messageService: MessageService,
    private activatedRoute: ActivatedRoute,
    override router: Router,
    override imageService: ImageService,
    override config: PrimeNGConfig,
    override fileService: FileControllerService
  ) {
    super(router, config, imageService, fileService);

    this.documentId = 'CAMPANHAS';

    this.form = this.formBuilder.group({
      title: [
        '',
        [
          Validators.required,
          Validators.maxLength(GlamCampaignsFormComponent.TITLE_MAX_LENGTH)
        ]
      ],
      minimumGlampoints: [null, [Validators.required, Validators.min(0)]],
      maximumGlampoints: [null, [Validators.required, Validators.min(0)]],
      description: [
        '',
        [
          Validators.required,
          Validators.maxLength(
            GlamCampaignsFormComponent.DESCRIPTION_MAX_LENGTH
          )
        ]
      ],
      hashtagCode: ['', [Validators.required]],
      image: ['', [Validators.required]],
      steps: this.formBuilder.array([this.createStepControl()])
    });
  }

  async ngOnInit(): Promise<void> {
    await this.getDocument();
  }

  override async getDocument(): Promise<void> {
    try {
      this.pageDocument = (await this.shopSettingsRepository.get({
        id: this.documentId
      })) as CampaignsDocument;
    } catch (error) {
      this.pageDocument = {
        id: this.documentId,
        campaigns: []
      } as CampaignsDocument;
    }

    this.normalizeDocument(this.pageDocument);
    this.campaigns = this.pageDocument.campaigns;

    if (this.router.url.includes('edit')) {
      this.activatedRoute.params.subscribe((params) => {
        this.activeIndex = +params['index'];
        this.activeCampaign = this.campaigns[this.activeIndex] || null;
        this.initializeForm();
      });
    }
  }

  override initializeForm(): void {
    const campaign = this.activeCampaign;
    if (!campaign) {
      return;
    }

    this.form.patchValue({
      title: campaign.title,
      minimumGlampoints: campaign.minimumGlampoints,
      maximumGlampoints: campaign.maximumGlampoints,
      description: campaign.description,
      hashtagCode: campaign.hashtagCode,
      image: campaign.image
    });

    this.steps.clear();

    if (campaign.steps?.length) {
      campaign.steps.forEach((step) => {
        this.steps.push(this.createStepControl(step));
      });
    } else {
      this.steps.push(this.createStepControl());
    }
  }

  override async save() {
    if (this.form.invalid || this.isGlampointsRangeInvalid()) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue = this.form.getRawValue();
    const campaign: Campaign = {
      title: formValue.title,
      minimumGlampoints: Number(formValue.minimumGlampoints),
      maximumGlampoints: Number(formValue.maximumGlampoints),
      description: formValue.description,
      hashtagCode: formValue.hashtagCode,
      image: formValue.image,
      steps: formValue.steps
    };

    if (this.activeCampaign && this.activeIndex >= 0) {
      this.campaigns[this.activeIndex] = campaign;
    } else {
      this.campaigns.push(campaign);
    }

    const payload: CampaignsUpdatePayload = {
      id: this.documentId,
      campaigns: this.campaigns || []
    };

    await this.shopSettingsRepository.update(payload);

    this.messageService.add({
      severity: 'success',
      summary: 'Salvo com sucesso',
      detail: 'As informações da campanha foram salvas.'
    });

    await this.afterSubmit();
    this.navigate('/configuracoes/glam/campaigns');
  }

  get steps(): FormArray<FormControl<string>> {
    return this.form.get('steps') as FormArray<FormControl<string>>;
  }

  addStep(): void {
    this.steps.push(this.createStepControl());
  }

  removeStep(index: number): void {
    if (this.steps.length === 1) {
      this.steps.at(0).reset('');
      this.steps.at(0).markAsTouched();
      return;
    }

    this.steps.removeAt(index);
  }

  isControlInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!control?.invalid && !!(control?.touched || control?.dirty);
  }

  isStepInvalid(index: number): boolean {
    const control = this.steps.at(index);
    return !!control?.invalid && !!(control?.touched || control?.dirty);
  }

  isGlampointsRangeInvalid(): boolean {
    const minimum = this.form.get('minimumGlampoints')?.value;
    const maximum = this.form.get('maximumGlampoints')?.value;

    if (minimum == null || maximum == null) {
      return false;
    }

    return Number(minimum) > Number(maximum);
  }

  async handleFileSelect(
    event: { files: File[] },
    inputName: string,
    expectedWidth: number,
    expectedHeight: number,
    maxWeight: number,
    pInput: FileUpload
  ) {
    this.fileNamePattern = `campanha-glam-${new Date().getTime()}.webp`;

    await this.upload(
      event,
      inputName,
      expectedWidth,
      expectedHeight,
      maxWeight,
      pInput
    );
  }

  goToMenu() {
    this.navigate('configuracoes/glam/campaigns');
  }

  get campaignImageUrl(): string {
    return this.form.get('image')?.value;
  }

  get titleMaxLength(): number {
    return GlamCampaignsFormComponent.TITLE_MAX_LENGTH;
  }

  get descriptionMaxLength(): number {
    return GlamCampaignsFormComponent.DESCRIPTION_MAX_LENGTH;
  }

  override get filePath(): string {
    return '/pages/campanhas/';
  }

  override get model() {
    return this.pageDocument;
  }

  override get modelId() {
    return this.pageDocument?.id;
  }

  private createStepControl(value = ''): FormControl<string> {
    return this.formBuilder.control(value, {
      nonNullable: true,
      validators: [Validators.required]
    });
  }

  private normalizeDocument(document: CampaignsDocument): void {
    document.campaigns = Array.isArray(document?.campaigns)
      ? document.campaigns
      : [];
  }
}
